/*
 * Delta Don — homepage motion layer (dd-home-fx.js).
 *
 * One effect, desktop-pointer only and fully gated:
 *   'Find your type' cursor feedback: each card's image nudges toward the
 *   cursor while hovered (tactile feel), combined with the CSS hover zoom.
 *
 * (The hero media parallax was removed — the hero background is static.)
 *
 * Everything runs through one rAF loop with passive listeners, writes only
 * transform, and shuts down entirely when:
 *   - the template is not the homepage,
 *   - the pointer is coarse / hover is unavailable,
 *   - the user prefers reduced motion (also re-checked live on change).
 * Styles live in assets/dd-home-fx.css.
 */

(() => {
  const main = document.querySelector('main[data-template="index"]');
  if (!main) return;

  const motionOK = matchMedia('(prefers-reduced-motion: no-preference)');
  const hoverOK = matchMedia('(hover: hover) and (pointer: fine)');

  const effectCards = [
    ...main.querySelectorAll('[id$="__dd_effects"] .card'),
  ];

  if (effectCards.length === 0) return;

  let enabled = motionOK.matches && hoverOK.matches;
  let rafId = null;

  /* Pending transform targets, consumed by the rAF loop. */
  const cardTargets = new Map(); // img -> {x, y} | null

  const CARD_RANGE = 6; // px of in-card image drift
  const CARD_ZOOM = 1.06; // mirrors the CSS hover zoom in dd-home-fx.css

  function paint() {
    rafId = null;
    if (!enabled) return;

    for (const [img, target] of cardTargets) {
      if (target) {
        img.style.transform = `translate3d(${target.x.toFixed(2)}px, ${target.y.toFixed(
          2,
        )}px, 0) scale(${CARD_ZOOM})`;
      } else {
        img.style.transform = '';
      }
    }
  }

  function schedule() {
    if (rafId === null && enabled) rafId = requestAnimationFrame(paint);
  }

  function clearAll() {
    for (const img of cardTargets.keys()) img.style.transform = '';
    cardTargets.clear();
  }

  /* ── Effects cards: cursor feedback ────────────────────────────────── */
  for (const card of effectCards) {
    const img = card.querySelector('.image-block img');
    if (!img) continue;

    card.addEventListener(
      'pointermove',
      (event) => {
        if (!enabled) return;
        const rect = card.getBoundingClientRect();
        cardTargets.set(img, {
          x: ((event.clientX - rect.left) / rect.width - 0.5) * CARD_RANGE,
          y: ((event.clientY - rect.top) / rect.height - 0.5) * CARD_RANGE,
        });
        schedule();
      },
      { passive: true },
    );

    card.addEventListener('pointerleave', () => {
      // Clearing the inline transform hands the hover zoom back to CSS.
      cardTargets.set(img, null);
      schedule();
    });
  }

  /* ── Enable / disable plumbing ─────────────────────────────────────── */
  function applyState() {
    enabled = motionOK.matches && hoverOK.matches;
    if (!enabled) clearAll();
    else schedule();
  }

  motionOK.addEventListener('change', applyState);
  hoverOK.addEventListener('change', applyState);
  applyState();
})();
